import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiClock, FiZap, FiArrowRight, FiSliders } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import DateRangePicker from '../../components/DateRangePicker';
import DatePicker from '../../components/DatePicker';
import ClockTimePicker from '../../components/ClockTimePicker';
import { useCreateRoundWizard } from '../../contexts/CreateRoundWizardContext';
import { getSchedulingWeekRange } from '../../utils/dateHelpers';

const PRESETS = [
  { id: 'this_week', label: 'This Week',   sub: 'Find the best time before the week ends', icon: FiZap },
  { id: 'next_week', label: 'Next Week',   sub: 'Plan further ahead',                    icon: FiArrowRight },
  { id: 'custom',    label: 'Custom Date Range', sub: 'Pick your own window',            icon: FiSliders },
];

const sameRange = (a, b) => (
  !!a && !!b &&
  +new Date(a.startDate) === +new Date(b.startDate) &&
  +new Date(a.endDate)   === +new Date(b.endDate)
);

function StepWhen() {
  const navigate = useNavigate();
  const { state, setWhen } = useCreateRoundWizard();
  const isSmart = state.mode === 'smart';

  // Track which preset the user picked so the custom drawer only opens when
  // the user explicitly selects "Custom Date Range" - otherwise it would
  // pop open any time `state.dateRange` happens not to match a preset.
  const detectInitialPreset = () => {
    if (!state.dateRange) return 'this_week';
    if (sameRange(state.dateRange, getSchedulingWeekRange(0))) return 'this_week';
    if (sameRange(state.dateRange, getSchedulingWeekRange(1))) return 'next_week';
    return 'custom';
  };
  const [preset, setPreset] = useState(detectInitialPreset);

  // Initialise default range for smart mode if empty
  useEffect(() => {
    if (isSmart && !state.dateRange) {
      setWhen({ dateRange: getSchedulingWeekRange(0) });
    }
  }, [isSmart, state.dateRange, setWhen]);

  const handlePresetSelect = (id) => {
    setPreset(id);
    if (id === 'this_week') setWhen({ dateRange: getSchedulingWeekRange(0) });
    else if (id === 'next_week') setWhen({ dateRange: getSchedulingWeekRange(1) });
    // For 'custom' we leave whatever range is currently in state and let the
    // user adjust via the picker.
  };

  const handleDateChange = (date) => {
    if (!date) return setWhen({ date: null });
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    setWhen({ date: `${year}-${month}-${day}` });
  };

  const canContinue = useMemo(() => {
    if (isSmart) return !!(state.dateRange?.startDate && state.dateRange?.endDate);
    return !!(state.date && state.time);
  }, [isSmart, state.dateRange, state.date, state.time]);

  const handleNext = () => {
    navigate(`/create-round/${state.mode}/details`);
  };

  return (
    <div>
      <p className={`text-sm text-text-muted mb-6 ${isSmart ? 'hidden sm:block' : ''}`}>
        {isSmart
          ? "Pick a window - we'll find the best tee time based on weather and availability."
          : 'Pick the exact date and tee time you want to play.'}
      </p>

      {isSmart ? (
        <div className="space-y-3">
          {PRESETS.map((p) => {
            const selected = preset === p.id;
            return (
              <div key={p.id}>
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => handlePresetSelect(p.id)}
                  className={`w-full flex items-center text-left gap-3 p-4 rounded-xl border-2 transition-colors min-h-[60px] ${
                    selected ? 'border-accent bg-accent/5' : 'border-border-subtle bg-card hover:border-border-subtle'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    selected ? 'bg-accent text-white' : 'bg-surface text-text-secondary'
                  }`}>
                    <SafeIcon icon={p.icon} className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-base font-bold ${selected ? 'text-accent' : 'text-text-primary'}`}>{p.label}</h3>
                    <p className="hidden sm:block text-xs text-text-muted">{p.sub}</p>
                  </div>
                </motion.button>

                <AnimatePresence initial={false}>
                  {selected && p.id === 'custom' && (
                    <motion.div
                      key="custom-picker"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 bg-card rounded-xl border border-border-subtle p-4 sm:p-5">
                        <label className="block text-sm font-semibold text-text-secondary mb-3">
                          <SafeIcon icon={FiCalendar} className="w-4 h-4 inline mr-2" />
                          Date range
                        </label>
                        <DateRangePicker
                          startDate={state.dateRange?.startDate || null}
                          endDate={state.dateRange?.endDate || null}
                          onChange={(startDate, endDate) => setWhen({ dateRange: { startDate, endDate } })}
                          placeholder="Select date range"
                          className="w-full"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label className="block text-sm font-semibold text-text-secondary mb-3">
              <SafeIcon icon={FiCalendar} className="w-4 h-4 inline mr-2" />
              Date
            </label>
            <DatePicker
              selectedDate={state.date ? new Date(state.date) : null}
              onChange={handleDateChange}
              placeholder="Select a date"
              className="w-full"
              quickSchedule
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-text-secondary mb-3">
              <SafeIcon icon={FiClock} className="w-4 h-4 inline mr-2" />
              Tee time
            </label>
            <ClockTimePicker
              selectedTime={state.time || ''}
              onChange={(t) => setWhen({ time: t })}
              placeholder="Select tee time"
              className="w-full"
            />
          </div>
        </div>
      )}

      <div className="mt-8 flex justify-end">
        <button
          onClick={handleNext}
          disabled={!canContinue}
          className="px-6 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-all duration-200 font-semibold disabled:opacity-50 min-h-[44px]"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

export default StepWhen;
